import { useEffect, useState } from 'react';
import { chargebeeService } from '@/services/chargebeeService';
import type { ChargebeeInstance } from '@/types/chargebee';
import type { ChargebeePK } from '@/types/subscription';
import { logError } from '@/utils/logger';

interface UseChargebeeReturn {
  cbInstance: ChargebeeInstance | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Lazily loads Chargebee.js (route-scoped — the one-time checkout bundle never
 * pulls it) and initializes it with the per-session keys delivered on the
 * subscription summary (`pks.chargebee`). The SDK itself lives in
 * {@link chargebeeService}; this hook only exposes its readiness to React.
 */
export const useChargebee = (chargebeeKeys?: ChargebeePK): UseChargebeeReturn => {
  const [cbInstance, setCbInstance] = useState<ChargebeeInstance | null>(null);
  const [error, setError] = useState<string | null>(null);

  const siteId = chargebeeKeys?.siteId;
  const publicKey = chargebeeKeys?.publicKey;

  useEffect(() => {
    if (!siteId || !publicKey) return;

    let cancelled = false;
    chargebeeService
      .initialize({ siteId, publicKey })
      .then((instance) => {
        if (cancelled) return;
        setCbInstance(instance);
      })
      .catch((err) => {
        logError('Chargebee SDK initialization failed', err);
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to initialize Chargebee');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [siteId, publicKey]);

  return { cbInstance, isLoading: !cbInstance && !error, error };
};
